import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey } from '@feature/utils';

import * as api from './DepartmentApi';
import { Department, DepartmentRequest } from './DepartmentTypes';

export const departmentKey = getQueryKey('department');

export function getDepartmentPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Department>> {
  return {
    queryKey: departmentKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getDepartmentOptions(departmentId: Id): UseSuspenseQueryOptions<Department> {
  return {
    queryKey: departmentKey.one(departmentId),
    queryFn: () => api.get(departmentId),
  };
}

export function getDepartmentAsHeadOfOptions(): UseSuspenseQueryOptions<Department[]> {
  return {
    queryKey: departmentKey.as(['list', 'head-of']),
    queryFn: () => api.getDepartmentsAsHeadOf(),
  };
}

export function getAccessibleDepartmentOptions(): UseSuspenseQueryOptions<Department[]> {
  return {
    queryKey: departmentKey.as(['list', 'accessible']),
    queryFn: () => api.getAccessibleDepartments(),
  };
}

export function getDepartmentByFacultyOptions(facultyId: number): UseSuspenseQueryOptions<Department[]> {
  return {
    queryKey: departmentKey.as(['faculty', facultyId]),
    queryFn: () => api.getDepartmentsByFaculty(facultyId),
  };
}

export function useDepartmentCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DepartmentRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: departmentKey.root(),
      }),
  });
}

export function useDepartmentEdit(departmentId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DepartmentRequest) => api.edit(departmentId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: departmentKey.root(),
      }),
  });
}
